/**
 * Carousel — minimal, manually-controlled image/instruction carousel.
 *
 * Used by the "Set Meraki API Key" page to walk the user through how to
 * generate a key in the Meraki dashboard. No autoplay (instructional content
 * — the user controls the pace) and keyboard navigation via arrow keys.
 */
import { useEffect, useState, type ReactNode } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { cn } from "@/lib/cn";

export interface CarouselSlide {
  /** Unique id used as the React key. */
  id: string;
  /** Image source URL (resolved by Vite from imports). */
  imageSrc: string;
  /** Alt text — also surfaces if the image fails to load. */
  imageAlt: string;
  /** Headline shown beneath the image. */
  title: string;
  /** Body copy. Plain ReactNode so we can inline links. */
  body: ReactNode;
}

export interface CarouselProps {
  slides: CarouselSlide[];
  className?: string;
}

export function Carousel({ slides, className }: CarouselProps) {
  const [active, setActive] = useState(0);
  const clampedActive = active < slides.length ? active : 0;

  // Arrow-key navigation. Wraps around at the ends — feels natural for short lists.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "ArrowLeft") setActive((i) => (i - 1 + slides.length) % slides.length);
      if (e.key === "ArrowRight") setActive((i) => (i + 1) % slides.length);
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [slides.length]);

  if (slides.length === 0) return null;
  const slide = slides[clampedActive]!

  return (
    <div className={cn("relative overflow-hidden rounded-2xl border border-line bg-surface", className)}>
      {/* Image area */}
      <div className="relative aspect-[16/10] w-full bg-bg-0">
        <img
          src={slide.imageSrc}
          alt={slide.imageAlt}
          className="h-full w-full object-cover"
          loading="lazy"
        />
        {/* Subtle dark gradient at the bottom so caption text reads well. */}
        <div className="pointer-events-none absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-bg-0/80 to-transparent" />
      </div>

      {/* Caption */}
      <div className="px-6 py-5">
        <div className="mb-1 font-mono text-[10px] uppercase tracking-[0.14em] text-fg-mute">
          Step {clampedActive + 1} of {slides.length}
        </div>
        <h3 className="m-0 text-base font-bold tracking-tight text-fg">{slide.title}</h3>
        <div className="mt-1.5 text-sm leading-relaxed text-fg-dim">{slide.body}</div>
      </div>

      {/* Bottom controls — prev/next + dot indicators. */}
      <div className="flex items-center justify-between gap-3 border-t border-line bg-surface-tint/40 px-4 py-3">
        <button
          type="button"
          onClick={() => setActive((i) => (i - 1 + slides.length) % slides.length)}
          aria-label="Previous slide"
          className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-fg-dim hover:bg-surface-2 hover:text-fg"
        >
          <ChevronLeft size={16} />
        </button>

        <div className="flex items-center gap-1.5" role="tablist">
          {slides.map((s, i) => (
            <button
              key={s.id}
              type="button"
              role="tab"
              aria-selected={i === clampedActive}
              aria-label={`Go to slide ${i + 1}`}
              onClick={() => setActive(i)}
              className={cn(
                "h-1.5 rounded-full transition-all",
                i === clampedActive ? "w-6 bg-aurora-grad" : "w-1.5 bg-fg-mute/40 hover:bg-fg-mute",
              )}
            />
          ))}
        </div>

        <button
          type="button"
          onClick={() => setActive((i) => (i + 1) % slides.length)}
          aria-label="Next slide"
          className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-fg-dim hover:bg-surface-2 hover:text-fg"
        >
          <ChevronRight size={16} />
        </button>
      </div>
    </div>
  );
}
